import React from 'react';
import './Blog.css';

// ─────────────────────────────────────────────────────────────────────────────
// BLOG POSTS DATA — Add new posts here. The grid always shows the first 4.
//
// Each post supports:
//   title     : string  (required)
//   excerpt   : string  (required) — short description shown on card
//   category  : string  (required) — tag badge on image
//   date      : string  (required) — e.g. 'Mar 20, 2026'
//   readTime  : string  (required) — e.g. '4 min read'
//   author    : string  (required) — shown in card footer
//   image     : string  (optional) — path/URL; placeholder shown if empty
//   link      : string  (optional) — URL to full blog post
//
// To add a new post, just add a new object to the array below.
// The most recently added post should go at the TOP of the array
// so it appears first on the website.
//
// Example:
//   {
//     title:    'Understanding Ground Settlement in Singapore Soils',
//     excerpt:  'A deep dive into how marine clay behaves under load...',
//     category: 'Geotechnical',
//     date:     'Apr 5, 2026',
//     readTime: '5 min read',
//     author:   'K. Nedunchezian',
//     image:    '/images/blog/ground-settlement.jpg',
//     link:     '/blog/ground-settlement',
//   },
// ─────────────────────────────────────────────────────────────────────────────

const BLOG_POSTS = [
  {
    title:    'What is TAM Grouting and Why Does It Matter?',
    excerpt:
      'Tube-à-manchette grouting is one of the most precise ground improvement methods. We explain how it works and when it is used in Singapore infrastructure.',
    category: 'Grouting',
    date:     'Mar 10, 2026',
    readTime: '5 min read',
    author:   'GSK Geotech Team',
    image:    '',   // replace with '/images/blog/tam-grouting.jpg'
    link:     '#',
  },
  {
    title:    'Recharge Wells: Protecting Buildings During Deep Excavations',
    excerpt:
      'When dewatering lowers the water table, nearby structures are at risk. Learn how recharge wells help maintain groundwater balance during construction.',
    category: 'Groundwater',
    date:     'Feb 18, 2026',
    readTime: '4 min read',
    author:   'M. Subenthiran',
    image:    '',
    link:     '#',
  },
  {
    title:    'Instrumentation & Monitoring on the DTSS Phase 2 Project',
    excerpt:
      'A look at how GSK Geotech deployed instrumentation and monitoring solutions during one of Singapores largest deep tunnel sewer projects.',
    category: 'Case Study',
    date:     'Jan 22, 2026',
    readTime: '6 min read',
    author:   'GSK Geotech Team',
    image:    '',
    link:     '#',
  },
  {
    title:    'Soil Investigation 101: What Happens Before Construction Begins',
    excerpt:
      'Before any foundation is laid, engineers need to understand the ground. Here is a straightforward guide to what soil investigation involves.',
    category: 'Soil Investigation',
    date:     'Dec 14, 2025',
    readTime: '3 min read',
    author:   'K. Nedunchezian',
    image:    '',
    link:     '#',
  },
  // ── Add more posts above this line ───────────────────────────────────────
  // The grid always shows the first 4 posts from this array.
  // Newest post should be at the TOP.
];

// ─────────────────────────────────────────────────────────────────────────────
// CONFIG — number of posts to show on the homepage section
// ─────────────────────────────────────────────────────────────────────────────
const POSTS_TO_SHOW = 4;

// Placeholder icon per category (used when no image is provided)
const CATEGORY_ICONS = {
  'Grouting':          '🔩',
  'Groundwater':       '💧',
  'Case Study':        '📋',
  'Soil Investigation':'🔬',
  'Ground Improvement':'🏗️',
  'Safety':            '🦺',
  'default':           '📰',
};

const getCategoryIcon = (category) =>
  CATEGORY_ICONS[category] || CATEGORY_ICONS['default'];

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
const Blog = () => {
  const visiblePosts = BLOG_POSTS.slice(0, POSTS_TO_SHOW);

  return (
    <section className="blog-section" id="blog">
      <div className="blog-container">

        {/* ── Header ── */}
        <div className="blog-header">
          <div className="blog-header-left">
            <div className="blog-eyebrow">
              <span className="blog-eyebrow-line" />
              Latest Insights
              <span className="blog-eyebrow-line" />
            </div>
            <h2 className="blog-title">
              News &amp; <span>Articles</span>
            </h2>
            <p className="blog-subtitle">
              Stay updated with the latest from GSK Geotech — project insights,
              technical guides, and industry news.
            </p>
          </div>

          <div className="blog-header-right">
            <a href="/blog" className="blog-view-all">
              View All Posts
              <span className="blog-view-all-arrow">→</span>
            </a>
          </div>
        </div>

        {/* ── Grid ── */}
        <div className="blog-grid">
          {visiblePosts.map((post) => (
            <article className="blog-card" key={post.title}>

              {/* Thumbnail */}
              <div className="blog-card-thumb">
                {post.image ? (
                  <img src={post.image} alt={post.title} />
                ) : (
                  <div className="blog-thumb-placeholder">
                    <span className="blog-thumb-placeholder-icon">
                      {getCategoryIcon(post.category)}
                    </span>
                    <span className="blog-thumb-placeholder-text">
                      Image Coming Soon
                    </span>
                  </div>
                )}
                <span className="blog-card-category">{post.category}</span>
              </div>

              {/* Body */}
              <div className="blog-card-body">
                <div className="blog-card-meta">
                  <span className="blog-card-date">{post.date}</span>
                  <span className="blog-card-meta-dot" />
                  <span className="blog-card-read-time">{post.readTime}</span>
                </div>

                <h3 className="blog-card-heading">{post.title}</h3>
                <p className="blog-card-excerpt">{post.excerpt}</p>

                <div className="blog-card-footer">
                  <span className="blog-card-author">{post.author}</span>
                  <a href={post.link || '#'} className="blog-card-link">
                    Read More →
                  </a>
                </div>
              </div>

            </article>
          ))}
        </div>

      </div>
    </section>
  );
};

export default Blog;